import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronRight } from 'lucide-react';
interface ManagerReviewProps {
  type: string;
  onApprove: () => void;
  onBack: () => void;
}
export const ManagerReview: React.FC<ManagerReviewProps> = ({
  type,
  onApprove,
  onBack
}) => {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const flaggedItems = [{
    id: 'cash',
    title: 'Cash Reconciliation',
    description: 'Register #1 has a variance of $5.25',
    status: 'flagged'
  }, {
    id: 'inventory',
    title: 'HVM Transfer',
    description: '2 items not scanned during transfer',
    status: 'flagged'
  }];
  const completedItems = [{
    id: 'security',
    title: 'Security Checklist',
    description: 'All security checks completed',
    status: 'completed'
  }];
  const [approvals, setApprovals] = useState<string[]>([]);
  const toggleApproval = (itemId: string) => {
    if (approvals.includes(itemId)) {
      setApprovals(prev => prev.filter(id => id !== itemId));
    } else {
      setApprovals(prev => [...prev, itemId]);
    }
  };
  const allItemsApproved = [...flaggedItems, ...completedItems].every(item => approvals.includes(item.id));
  return <div className="p-4 space-y-6" data-id="element-243">
      <div data-id="element-244">
        <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-245">
          Manager Review
        </h2>
        <p className="text-sm text-gray-500" data-id="element-246">
          Review and approve the closing process
        </p>
      </div>
      <div className="space-y-4" data-id="element-247">
        <h3 className="text-sm font-medium text-gray-900" data-id="element-248">
          Items Requiring Review
        </h3>
        {flaggedItems.map(item => <div key={item.id} className={`bg-white border ${approvals.includes(item.id) ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'} rounded-lg p-4`} data-id="element-249">
            <div className="flex justify-between items-center" data-id="element-250">
              <div className="flex items-center" data-id="element-251">
                {approvals.includes(item.id) ? <CheckCircle className="w-5 h-5 text-green-500 mr-3" data-id="element-252" /> : <AlertTriangle className="w-5 h-5 text-red-500 mr-3" data-id="element-253" />}
                <div data-id="element-254">
                  <h4 className="font-medium text-gray-900" data-id="element-255">{item.title}</h4>
                  <p className="text-sm text-gray-500" data-id="element-256">{item.description}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-257" />
            </div>
            <div className="mt-4 pt-3 border-t border-gray-200" data-id="element-258">
              <div className="flex justify-between items-center" data-id="element-259">
                <span className="text-sm text-gray-500" data-id="element-260">
                  {approvals.includes(item.id) ? 'Approved' : 'Needs approval'}
                </span>
                <button onClick={() => toggleApproval(item.id)} className={`px-3 py-1 rounded-md text-sm font-medium ${approvals.includes(item.id) ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-261">
                  {approvals.includes(item.id) ? 'Approved' : 'Approve'}
                </button>
              </div>
            </div>
          </div>)}
      </div>
      <div className="space-y-4" data-id="element-262">
        <h3 className="text-sm font-medium text-gray-900" data-id="element-263">Completed Items</h3>
        {completedItems.map(item => <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-264">
            <div className="flex justify-between items-center" data-id="element-265">
              <div className="flex items-center" data-id="element-266">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3" data-id="element-267" />
                <div data-id="element-268">
                  <h4 className="font-medium text-gray-900" data-id="element-269">{item.title}</h4>
                  <p className="text-sm text-gray-500" data-id="element-270">{item.description}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-271" />
            </div>
            <div className="mt-4 pt-3 border-t border-gray-200" data-id="element-272">
              <div className="flex justify-between items-center" data-id="element-273">
                <span className="text-sm text-gray-500" data-id="element-274">
                  {approvals.includes(item.id) ? 'Reviewed' : 'Needs review'}
                </span>
                <button onClick={() => toggleApproval(item.id)} className={`px-3 py-1 rounded-md text-sm font-medium ${approvals.includes(item.id) ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-275">
                  {approvals.includes(item.id) ? 'Reviewed' : 'Review'}
                </button>
              </div>
            </div>
          </div>)}
      </div>
      <div className="pt-4 space-y-4" data-id="element-276">
        <div className="flex items-center" data-id="element-277">
          <input id="confirm-review" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-278" />
          <label htmlFor="confirm-review" className="ml-2 block text-sm text-gray-900" data-id="element-279">
            I confirm I have reviewed all items and approve this closing report
          </label>
        </div>
        <div className="flex space-x-3" data-id="element-280">
          <button onClick={onBack} className="flex-1 bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-281">
            Back
          </button>
          <button onClick={onApprove} className={`flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors ${!allItemsApproved && 'opacity-60'}`} disabled={!allItemsApproved} data-id="element-282">
            Finalize & Approve
          </button>
        </div>
      </div>
    </div>;
};